// src/services/api/dashboard.service.ts
import { authService } from '../auth.service';
import { ReferralData, ReferralActivation } from "../../types/application.types";
import { getServerUrl, getCurrentApiUrl } from '../../config/server.config';

interface MasterCardData {
    success: boolean;
    data: {
        data: {
            data: any
        }
    }
}
interface InitTopUpData {
    operationId: string;
    operationNumber: string;
    status: string;
    masterWalletAddress: string;
    masterWalletType: string;
}
interface QuoteData {
    operationId: string;
    cryptoAmount: string;
    cryptoSymbol: string;
    exchangeRate: number;
    fiatAmount: number;
    fiatCurrency: string;
    networkFee: number
    networkFeeFiat: number
    commissionPercentage: number,
    commissionAmount: number;
    netFiatAmount: number;
    expiresAt: string;
}
type Card = {
    id: number;
    exp: string; // e.g., "2030-06-30"
    type: 'VIRTUAL' | 'PHYSICAL'; // if there are other types, you can extend this union
    activationDate: string | null;
    configId: string;
    externalCardId: string;
    dcUserId: number;
    dcCorporationId: number | null;
    balanceId: string;
    cardNo?: string;
    maskedPan?: string;
};

type PaginationLink = {
    url: string | null;
    label: string;
    active: boolean;
};

type Links = {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
};

type Meta = {
    current_page: number;
    from: number;
    last_page: number;
    links: PaginationLink[];
    path: string;
    per_page: number;
    to: number;
    total: number;
};

type CardsResponse = {
    success: boolean;
    message: string;
    data: {
        data: Card[];
        links: Links;
        meta: Meta;
    };
};
type ResponseCardBalance = {
    success: boolean;
    data: {
        balance: number;
        currency: string;
    }
}
interface TopUpData {
    amount: number,
    railAccountId: string
}
interface ResponseTopUpData {
    success: boolean,
    message: string,
    data: any[];
}
// interface InitGkhanData {

// }
class CardService {

    private get baseUrl(): string {
        return getServerUrl(true);
    }

    async getMasterCardData(): Promise<MasterCardData> {
        try {
            const response = await fetch(`${this.baseUrl}/providers/verestro/cards`, {
                method: 'GET',
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                },
            });
            
            if (!response.ok) {
                console.error(`Failed to fetch MasterCard data: ${response.status}`);
                return {
                    success: false,
                    data: {
                        data: {
                            data: []
                        }
                    }
                };
            }
            
            const result = await response.json();
            return result || {
                success: false,
                data: {
                    data: {
                        data: []
                    }
                }
            };
        } catch (error) {
            console.error('Error fetching MasterCard data:', error);
            return {
                success: false,
                data: {
                    data: {
                        data: []
                    }
                }
            };
        }
    }
    async getCardInit(data): Promise<InitTopUpData> {
        const response = await fetch(`${this.baseUrl}/crypto/operations/initiate`, {
            method: 'POST',
            headers: {
                'Authorization': authService.getAuthorizationHeader(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        const initdata = await response.json();
        return initdata.data;
    }
    async getCardQuote(data): Promise<QuoteData> {
        const response = await fetch(`${this.baseUrl}/crypto/operations/quote`, {
            method: 'POST',
            headers: {
                'Authorization': authService.getAuthorizationHeader(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        const quotedata = await response.json();
        return quotedata.data;
    }
    async confirmCard(id: string): Promise<any> {
        const confirmResponse = await
            fetch(`${this.baseUrl}/crypto/operations/confirm`, {
                method: 'POST',
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    operationId: id
                })
            });
        const confirmdata = await confirmResponse.json();
        return confirmdata;
    }
    async getMasterCardBalance(cardId: string): Promise<ResponseCardBalance> {
        try {
            const response = await fetch(`${this.baseUrl}/providers/verestro/cards/${cardId}/balance`, {
                method: 'GET',
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Error fetching card balance: ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {

            console.log(error);
        }
    };
    async getCardData(): Promise<any> {
        try {
            const response = await fetch(`${this.baseUrl}/providers/verestro/cards`, {
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch cards: ${response.status}`);
            }

            const data = await response.json();
            if (data.success && data.data) {
                if (data.data.data && Array.isArray(data.data.data)) {
                    return data.data.data;
                } else if (data.data.content && Array.isArray(data.data.content)) {
                    return data.data.content;
                } else if (Array.isArray(data.data)) {
                    return data.data;
                }
            }
            return [];
        } catch (error) {
            console.error('Error activating referrer:', error);
        }
    }
    async getCardItem(card:Card): Promise<Card> {
        try {
            // Consulta detallada igual que en CardDetail.tsx
            const response = await fetch(`${getCurrentApiUrl()}/providers/verestro/cards/${card.id}`, {
              headers: {
                'Authorization': authService.getAuthorizationHeader(),
                'Content-Type': 'application/json'
              }
            });
            
            if (!response.ok) return card;
            
            const data = await response.json();
            
            if (data.success && data.data) {
              return {
                ...card,
                // Priorizar cardNo sobre maskedPan para obtener el número completo si está disponible
                cardNo: data.data.cardNo || data.data.cardNumber,
                maskedPan: data.data.maskedPan
              };
            }
            
            return card;
          } catch (error) {
            console.error(`Error fetching details for card ${card.id}:`, error);
            return card;
          }
    }

    /**
     * Calculate crypto reload costs
     * GET /api/providers/verestro/cards/:cardId/reload-costs
     */
    async getCryptoReloadCosts(cardId: string, params: {
        network: string;
        token?: string;
        amountEUR: number;
    }): Promise<{
        amountEUR: number;
        commissionRate: number;
        commissionEUR: number;
        netAmountEUR: number;
        cryptoAmount: number;
        networkFee: number;
        totalCrypto: number;
        exchangeRate: number;
        cryptoSymbol: string;
    }> {
        const queryParams = new URLSearchParams({
            network: params.network,
            amountEUR: params.amountEUR.toString(),
        });

        if (params.token) {
            queryParams.append('token', params.token);
        }

        const response = await fetch(
            `${getCurrentApiUrl()}/providers/verestro/cards/${cardId}/reload-costs?${queryParams}`,
            {
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to calculate reload costs');
        }

        const data = await response.json();
        return data.data;
    }

    /**
     * Create crypto reload request
     * POST /api/providers/verestro/cards/:cardId/crypto-reload
     */
    async createCryptoReload(cardId: string, params: {
        fromWallet: string;
        network: string;
        token?: string;
        amountEUR: number;
    }): Promise<{
        reloadId: string;
        txHash: string;
        status: string;
        costs: any;
        estimatedTime: string;
    }> {
        const response = await fetch(
            `${getCurrentApiUrl()}/providers/verestro/cards/${cardId}/crypto-reload`,
            {
                method: 'POST',
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(params)
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create crypto reload');
        }

        const data = await response.json();
        return data.data;
    }

    /**
     * Get crypto reload history
     * GET /api/providers/verestro/my-crypto-reloads
     */
    async getMyCryptoReloads(params?: {
        page?: number;
        limit?: number;
    }): Promise<any> {
        const queryParams = new URLSearchParams({
            page: (params?.page || 1).toString(),
            limit: (params?.limit || 10).toString(),
        });

        const response = await fetch(
            `${getCurrentApiUrl()}/providers/verestro/my-crypto-reloads?${queryParams}`,
            {
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to fetch crypto reload history');
        }

        const data = await response.json();
        return data.data;
    }

    /**
     * Calculate Zenus topup costs (preview)
     * POST /api/providers/verestro/topup-zenus/calculate
     */
    async calculateZenusTopupCosts(amountEUR: number): Promise<{
        amountEUR: number;
        bankCommissionRate: number;
        bankCommissionEUR: number;
        totalEUR: number;
        exchangeRate: number;
        totalUSD: number;
    }> {
        const response = await fetch(
            `${getCurrentApiUrl()}/providers/verestro/topup-zenus/calculate`,
            {
                method: 'POST',
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ amountEUR })
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to calculate topup costs');
        }

        const data = await response.json();
        return data.data;
    }

    /**
     * Process Zenus topup
     * POST /api/providers/verestro/topup-zenus
     */
    async processZenusTopup(params: {
        cardId: string;
        amountEUR: number;
        zenusAccountId: string;
    }): Promise<{
        topupId: string;
        status: string;
        amountEUR: number;
        totalUSD: number;
        exchangeRate: number;
    }> {
        const response = await fetch(
            `${getCurrentApiUrl()}/providers/verestro/topup-zenus`,
            {
                method: 'POST',
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(params)
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to process topup');
        }

        const data = await response.json();
        return data.data;
    }

    /**
     * Get Zenus topup history
     * GET /api/providers/verestro/topup-zenus/history
     */
    async getZenusTopupHistory(params?: {
        page?: number;
        limit?: number;
    }): Promise<{
        topups: any[];
        pagination: {
            total: number;
            page: number;
            limit: number;
            totalPages: number;
        };
    }> {
        const queryParams = new URLSearchParams({
            page: (params?.page || 1).toString(),
            limit: (params?.limit || 10).toString(),
        });

        const response = await fetch(
            `${getCurrentApiUrl()}/providers/verestro/topup-zenus/history?${queryParams}`,
            {
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to fetch topup history');
        }

        const data = await response.json();
        return data.data;
    }
}

export const cardService = new CardService();